import { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useSessionStore } from '../store';

/**
 * Hook to detect first-time authenticated users and control the onboarding survey modal.
 * Returns { shouldShowSurvey, dismissSurvey }.
 */
export const useOnboardingSurvey = () => {
  const { isLoaded, isSignedIn, user } = useUser();
  const userAuthState = useSessionStore((state) => state.userAuthState);
  const [ready, setReady] = useState(false);

  // Add a 500ms delay after auth state settles to avoid flashing during redirect navigation
  useEffect(() => {
    if (
      isLoaded &&
      isSignedIn &&
      user &&
      userAuthState === 'authenticated' &&
      user.unsafeMetadata?.onboardingSurveyCompleted !== true
    ) {
      const timer = setTimeout(() => setReady(true), 500);
      return () => clearTimeout(timer);
    } else {
      setReady(false);
    }
  }, [isLoaded, isSignedIn, user, userAuthState]);

  const shouldShowSurvey =
    ready &&
    isLoaded &&
    isSignedIn &&
    !!user &&
    userAuthState === 'authenticated' &&
    user.unsafeMetadata?.onboardingSurveyCompleted !== true;

  const dismissSurvey = async (surveyData = null) => {
    if (!user) return;
    try {
      const updatedMetadata = {
        ...user.unsafeMetadata,
        onboardingSurveyCompleted: true,
      };
      if (surveyData) {
        updatedMetadata.surveyRoomUsage = surveyData.roomUsage;
        updatedMetadata.surveyRoomUsageOther = surveyData.roomUsageOther;
        updatedMetadata.surveyIdentity = surveyData.identity;
        updatedMetadata.surveyIdentityOther = surveyData.identityOther;
        updatedMetadata.surveyOtherComments = surveyData.otherComments;
        updatedMetadata.surveyCompletedAt = surveyData.completedAt;
      } else {
        updatedMetadata.onboardingSurveySkipped = true;
      }
      await user.update({ unsafeMetadata: updatedMetadata });
    } catch (error) {
      console.error('Failed to save onboarding survey:', error);
      throw error;
    }
  };

  return { shouldShowSurvey, dismissSurvey };
};
